import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../../components/Config/api.service';
import EmployeeSelect from '../../../components/Formulaire/EmployeeSelect';
import { FaTrash } from 'react-icons/fa';
import { notify } from '../../../components/Utils/Notifications';
import { FaRegTrashAlt } from 'react-icons/fa';
import ActionDropdown from '../../../components/Utils/ActionDropdown';

const HspForm = ({ setIsDrawerOpen, id, refreshHspList }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        selectedEmployee: null,
        selectedMonth: new Date().toISOString().slice(0, 7),
        dailyHsp: [{ id: null, date: "", start_time: "", end_time: "", is_holyday: false }],
        overtimeLines: [], // NEW: lignes manuelles par config
        manuallyEditedHours: false
    });
    const [overtimeConfigs, setOvertimeConfigs] = useState([]); // NEW: configurations dynamiques
    const [allowDateOutsideMonth, setAllowDateOutsideMonth] = useState(false);
    const [employeeHireDate, setEmployeeHireDate] = useState('');
    const [loading, setLoading] = useState(false);

    const { dailyHsp, selectedMonth, selectedEmployee, manuallyEditedHours, overtimeLines } = formData;

    const normalizeDateValue = (value) => {
        if (!value) return '';
        if (typeof value === 'string' && value.includes('/')) {
            const parts = value.split('/');
            if (parts.length === 3) {
                const [day, month, year] = parts;
                return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            }
            if (parts.length === 2) {
                const [month, year] = parts;
                return `${year}-${month.padStart(2, '0')}-01`;
            }
        }
        return String(value).slice(0, 10);
    };

    const getNextDay = (dateValue) => {
        if (!dateValue) return '';
        const date = new Date(`${dateValue}T00:00:00`);
        if (Number.isNaN(date.getTime())) return '';
        date.setDate(date.getDate() + 1);
        return date.toISOString().slice(0, 10);
    };

    const getSelectedMonthBounds = (monthValue) => {
        if (!monthValue) {
            return { start: '', end: '' };
        }

        const [year, month] = monthValue.split('-').map(Number);
        if (!year || !month) {
            return { start: '', end: '' };
        }

        const paddedMonth = String(month).padStart(2, '0');
        const start = `${year}-${paddedMonth}-01`;
        const lastDay = new Date(year, month, 0).getDate();
        const end = `${year}-${paddedMonth}-${String(lastDay).padStart(2, '0')}`;

        return { start, end };
    };

    const getDateConstraints = () => {
        const hireMin = employeeHireDate ? getNextDay(employeeHireDate) : '';

        if (allowDateOutsideMonth) {
            // Le mois concerné reste indicatif : seule la date d'embauche continue de borner la saisie.
            return { min: hireMin, max: '' };
        }

        const { start: monthStart, end: monthEnd } = getSelectedMonthBounds(selectedMonth);
        const min = hireMin && monthStart ? (hireMin > monthStart ? hireMin : monthStart) : (hireMin || monthStart);
        return {
            min,
            max: monthEnd,
        };
    };

    const isDateAllowed = (dateValue) => {
        if (!dateValue) return true;
        const { min, max } = getDateConstraints();
        if (min && dateValue < min) return false;
        if (max && dateValue > max) return false;
        return true;
    };

    const isDateOutsideSelectedMonth = (dateValue) => {
        if (!dateValue) return false;
        const { start: monthStart, end: monthEnd } = getSelectedMonthBounds(selectedMonth);
        return Boolean((monthStart && dateValue < monthStart) || (monthEnd && dateValue > monthEnd));
    };

    const updateFormData = useCallback((updates) => {
        setFormData(prev => ({ ...prev, ...updates }));
    }, []);

    const round = (value, decimals = 2) => {
        if (value === null || value === undefined || isNaN(Number(value))) return 0;
        return parseFloat(Number(value).toFixed(decimals));
    };

    // Charger les configurations pour l'UI
    useEffect(() => {
        apiService.getElement('/additionalHours?limit=1')
            .then(res => {
                setOvertimeConfigs(res.data.additionalHours_config || []);
                setAllowDateOutsideMonth(Boolean(res.data.allow_overtime_date_outside_month));
            })
            .catch(() => {
                setOvertimeConfigs([]);
            });
    }, []);

    useEffect(() => {
        const employeeId = selectedEmployee?.value;

        if (!employeeId) {
            setEmployeeHireDate('');
            return;
        }

        let isCancelled = false;

        apiService.getElementById('/employee/contracts', employeeId)
            .then(response => {
                const contracts = response.data.contracts || [];
                const openContract = contracts.find(contract => contract.Statut === 'open') || contracts[0] || null;
                const rawHireDate = openContract?.DateStart || openContract?.date_start || openContract?.start_date || openContract?.startDate || '';
                if (!isCancelled) {
                    setEmployeeHireDate(normalizeDateValue(rawHireDate));
                }
            })
            .catch(() => {
                if (!isCancelled) {
                    setEmployeeHireDate('');
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [selectedEmployee]);

    useEffect(() => {
        if (!manuallyEditedHours && !allowDateOutsideMonth) {
            const sanitizedDailyHsp = (dailyHsp || []).map(line => {
                if (!line.date) return line;
                return isDateAllowed(line.date)
                    ? line
                    : { ...line, date: '' };
            });

            const changed = sanitizedDailyHsp.some((line, index) => line.date !== (dailyHsp[index] || {}).date);
            if (changed) {
                updateFormData({ dailyHsp: sanitizedDailyHsp });
                notify(t('hspForm.datesResetNotice'), '', 3);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMonth, employeeHireDate, allowDateOutsideMonth]);

    // Charger la ressource si id
    useEffect(() => {
        if (id) {
            apiService.getElementById('/additionalHours', id)
                .then(response => {
                    const data = response.data.additionalHour;
                    // Construire overtimeLines depuis overtime_summary (édition manuelle)
                    const lines = (data.overtime_summary || []).map(l => ({
                        overtime_config_id: l.config_id,
                        overtime_config_code: l.config_code,
                        hours: round(l.hours || 0)
                    }));
                    const daily = data.dailyHsp || [];

                    updateFormData({
                        selectedEmployee: {
                            value: data.employee_id,
                            label: data.employee_name 
                        },
                        selectedMonth: data.month.includes('/') ? data.month.split('/').reverse().join('-') : data.month,
                        dailyHsp: daily.length ? daily : [{ id: null, date: "", start_time: "", end_time: "", is_holyday: false }],
                        overtimeLines: lines,
                        // Si pas de daily, on considère que c'est une saisie manuelle
                        manuallyEditedHours: daily.length === 0 && lines.length > 0
                    });
                })
                .catch(error => {
                    console.error('Error fetching additionalHour:', error);
                    notify(t('hspForm.fetchError'), '', 3);
                });
        } else {
            updateFormData({
                selectedEmployee: null,
                selectedMonth: new Date().toISOString().slice(0, 7),
                dailyHsp: [{ id: null, date: "", start_time: "", end_time: "", is_holyday: false }],
                overtimeLines: [],
                manuallyEditedHours: false
            });
        }
    }, [id, updateFormData]);

    // Modifier une ligne d’heures supp manuelles
    const handleOvertimeLineChange = (configId, value) => {
        console.log('handleOvertimeLineChange', configId, value);
        const numeric = round(parseFloat(value) || 0, 2);
        const next = [...(overtimeLines || [])];
        const idx = next.findIndex(l => l.overtime_config_id == configId);
        if (idx >= 0) {
            next[idx] = { ...next[idx], hours: numeric };
        } else {
            const cfg = overtimeConfigs.find(c => c.id == configId);
            next.push({
                overtime_config_id: configId,
                overtime_config_code: cfg?.code,
                hours: numeric
            });
        }
        updateFormData({
            overtimeLines: next,
            manuallyEditedHours: true // bascule en mode manuel dès saisie
        });
    };

    // Modifier une ligne journalière
    const handleDailyHspChange = (index, field, value) => {
        if (field === 'date' && value && !isDateAllowed(value)) {
            notify(t('hspForm.dateMustBelongToMonth'), '', 3);
            return;
        }

        if (field === 'date' && value && allowDateOutsideMonth && isDateOutsideSelectedMonth(value)) {
            notify(t('hspForm.dateOutsideMonthWarning'), '', 3);
        }

        const updated = [...dailyHsp];
        updated[index][field] = value;

        // Si on modifie la date, cocher automatiquement si c'est un weekend (samedi/dimanche).
        // L'utilisateur peut toujours décocher manuellement via le toggle (champ 'is_holyday').
        if (field === 'date') {
            let isHoliday = false;
            if (value) {
                const d = new Date(value);
                const day = d.getDay(); // 0 = dimanche, 6 = samedi
                if (day === 0 || day === 6) {
                    isHoliday = true;
                }
            }
            updated[index].is_holyday = isHoliday;
        }

        updateFormData({
            dailyHsp: updated,
            // rester en mode dates si on modifie les dates
            manuallyEditedHours: false
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!manuallyEditedHours) {
            const invalidMonthLine = (dailyHsp || []).find(line => line.date && !isDateAllowed(line.date));
            if (invalidMonthLine) {
                notify(
                    allowDateOutsideMonth
                        ? t('hspForm.allDatesMustRespectHireDate')
                        : t('hspForm.allDatesMustBelongToMonth'),
                    '', 3
                );
                return;
            }
        }

        if (!manuallyEditedHours && employeeHireDate) {
            const invalidLine = (dailyHsp || []).find(line => line.date && line.date <= employeeHireDate);
            if (invalidLine) {
                notify(t('hspForm.dateMustBeAfter', { date: employeeHireDate }), '', 3);
                return;
            }
        }

        const submissionData = {
            selectedEmployee: selectedEmployee ? selectedEmployee.value : null,
            selectedMonth: selectedMonth.split('-').reverse().join('/'),
            manuallyEditedHours
        };

        if (manuallyEditedHours) {
            // Envoyer les lignes manuelles
            submissionData.overtimeLines = (overtimeLines || [])
                .filter(l => (l.hours || 0) > 0)
                .map(l => ({
                    overtime_config_id: l.overtime_config_id,
                    hours: round(l.hours)
                }));
        } else {
            // Mode dates: laisser le serveur calculer
            submissionData.dailyHsp = dailyHsp || [];
        }

        const apiCall = id
            ? apiService.updateElementById('/additionalHours', id, submissionData)
            : apiService.saveElement('/additionalHour', submissionData);

        apiCall
            .then( response => {
                if (response.data?.error) {
                    notify(response.data.error, '', 3);
                    return;
                }
                notify(t('hspForm.saveSuccess'), '', 2);
                setIsDrawerOpen(false);
                refreshHspList();
            })
            .catch(error => {
                console.error(id ? 'Error updating additionalHour:' : 'Error creating additionalHour:', error);
                notify(error.userMessage || (id ? t('hspForm.updateError') : t('hspForm.createError')), '', 3);
            });
    };

    const addNewLine = () => {
        updateFormData({
            dailyHsp: [...dailyHsp, { id: null, date: "", start_time: "", end_time: "", is_holyday: false }]
        });
    };

    const removeLine = (index) => {
        updateFormData({
            dailyHsp: dailyHsp.filter((_, i) => i !== index)
        });
    };

    const handleDelete = async () => {
        if (!window.confirm(t('hspForm.deleteConfirm'))) {
            return;
        }
        try {
            const deletedFormData = { active: false };
            await apiService.updateElementById(`/additionalHours`, id, deletedFormData);
            notify(t('hspForm.deleteSuccess'), '', 2);
            setIsDrawerOpen(false);
            refreshHspList();
        } catch (error) {
            console.error('Error deleting additionalHours:', error);
            notify(error.userMessage || t('hspForm.deleteError'), '', 3);
        }
    };

    const dropdownActions = [
        {
            label: t('hspForm.delete'),
            onClick: handleDelete,
            disabled: loading || !id,
            color: 'text-red-500',
            icon: <FaRegTrashAlt size={16} />
        }
    ];

    // Helper pour valeur d’une config
    const getHoursForConfig = (configId) => {
        const line = (overtimeLines || []).find(l => l.overtime_config_id === configId);
        return line ? line.hours : 0;
    };

    // Calcul total heures manuelles (affichage UX)
    const totalManualHours = (overtimeLines || []).reduce((sum, l) => sum + (Number(l.hours) || 0), 0);

    return (
        <div className="p-6 h-screen bg-white text-base-content">
            <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold mb-4">{id ? t('hspForm.editTitle') : t('hspForm.addTitle')}</h2>
                {id && (
                    <ActionDropdown actions={dropdownActions} />
                )}
            </div>
            <form onSubmit={handleSubmit}>
                <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">{t('hspForm.employeeName')}</label>
                    <EmployeeSelect
                        value={selectedEmployee}
                        onChange={option => updateFormData({ selectedEmployee: option })}
                        placeholder={t('hspForm.chooseEmployee')}
                    />
                </div>
                <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">{t('hspForm.monthOf')}</label>
                    <input
                        type="month"
                        id="month"
                        name="month"
                        className="input input-bordered w-full"
                        value={selectedMonth}
                        onChange={(e) => updateFormData({ selectedMonth: e.target.value })}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                        {allowDateOutsideMonth
                            ? t('hspForm.byDatesModeHintOutsideAllowed')
                            : t('hspForm.byDatesModeHint')}
                    </p>
                </div>

                <div className="text-sm text-gray-600 mb-6">
                    {t('hspForm.vacationBonusHint')}
                </div>

                {/* Mode indicator */}
                {/* <div className="mb-4 flex items-center">
                    <span className="text-sm font-medium mr-2">Mode actuel :</span>
                    <span className="badge badge-primary">{manuallyEditedHours ? "Par Heures (configurations)" : "Par Dates"}</span>
                    {manuallyEditedHours ? (
                        <button
                            type="button"
                            className="ml-4 text-blue-600 text-sm hover:underline"
                            onClick={() => updateFormData({ manuallyEditedHours: false })}
                        >
                            Revenir au mode par dates
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="ml-4 text-blue-600 text-sm hover:underline"
                            onClick={() => updateFormData({ manuallyEditedHours: true })}
                        >
                            Passer au mode par taux
                        </button>
                    )}
                </div> */}

                {/* Mode selector - amélioré pour UX */}
                <div className="mb-6">
                    <label className="block text-sm font-medium mb-2">{t('hspForm.entryMode')}</label>

                    <div className="inline-flex items-center bg-gray-100 rounded-lg p-1 shadow-sm" role="tablist" aria-label={t('hspForm.entryMode')}>
                        <button
                            type="button"
                            role="tab"
                            aria-pressed={!manuallyEditedHours}
                            onClick={() => updateFormData({ manuallyEditedHours: false })}
                            className={`px-4 py-2 rounded-md transition text-sm font-medium ${!manuallyEditedHours ? 'bg-[--color-primary] text-white shadow' : 'text-gray-700 hover:bg-white'}`}
                        >
                            {t('hspForm.byDates')}
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-pressed={manuallyEditedHours}
                            onClick={() => updateFormData({ manuallyEditedHours: true })}
                            className={`ml-2 px-4 py-2 rounded-md transition text-sm font-medium ${manuallyEditedHours ? 'bg-[--color-primary] text-white shadow' : 'text-gray-700 hover:bg-white'}`}
                        >
                            {t('hspForm.manual')}
                        </button>
                    </div>

                    <div className="mt-2 flex items-center justify-between">
                        <p className="text-xs text-gray-500">
                            {!manuallyEditedHours
                                ? t('hspForm.byDatesDescription')
                                : t('hspForm.manualDescription')}
                        </p>
                    </div>
                </div>

                {/* Saisie des heures manuelles par configuration - affichée uniquement en mode manuel */}
                {manuallyEditedHours && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-yellow-50 p-4 rounded-md mb-6">
                        {(overtimeConfigs || []).map(cfg => (
                            <div key={cfg.id}>
                                <div className="text-sm font-semibold">
                                    {cfg.name}
                                </div>
                                <input
                                    type="number"
                                    step="0.1"
                                    className="input w-full mt-2"
                                    placeholder="0"
                                    value={getHoursForConfig(cfg.id)}
                                    onChange={(e) => handleOvertimeLineChange(cfg.id, e.target.value)}
                                />
                            </div>
                        ))}
                    </div>
                )}

                {/* Détail des dates - affiché uniquement en mode dates */}
                {!manuallyEditedHours && (
                    <div className="mb-6">
                        <h3 className="text-lg font-medium mb-2">{t('hspForm.dateDetails')}</h3>

                        {/* Légende : expliquer la couleur jaune */}
                        <div className="flex items-center gap-2 mb-3">
                            <span className="w-4 h-4 bg-yellow-50 border border-yellow-300 rounded-sm inline-block" aria-hidden="true"></span>
                            <span className="text-sm text-gray-600">{t('hspForm.yellowLineLegend')}</span>
                        </div>

                        <table className="table text-sm ">
                            <thead>
                                <tr>
                                    <th>{t('hspForm.date')}</th>
                                    <th>{t('hspForm.startTime')}</th>
                                    <th>{t('hspForm.endTime')}</th>
                                    <th>{t('hspForm.holidaysRest')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dailyHsp.map((line, index) => (
                                    <tr
                                        key={index}
                                        className={`transition ${line.is_holyday ? 'bg-yellow-50 border-l-4 border-yellow-300' : 'hover:bg-gray-50'}`}
                                    >
                                        <td style={{ width: "30%" }}>
                                            <div className="flex items-center">
                                                <input
                                                    type="date"
                                                    min={getDateConstraints().min || undefined}
                                                    max={getDateConstraints().max || undefined}
                                                    className={`input input-bordered w-full text-sm ${line.is_holyday ? 'bg-yellow-100/60' : ''}`}
                                                    value={line.date}
                                                    onChange={(e) => handleDailyHspChange(index, 'date', e.target.value)}
                                                />
                                            </div>
                                        </td>
                                         <td>
                                             <input
                                                 type="time"
                                                 className="input input-bordered w-full text-sm"
                                                 value={line.start_time}
                                                 onChange={(e) => handleDailyHspChange(index, 'start_time', e.target.value)}
                                             />
                                         </td>
                                         <td>
                                             <input
                                                 type="time"
                                                 className="input input-bordered w-full text-sm"
                                                 value={line.end_time}
                                                 onChange={(e) => handleDailyHspChange(index, 'end_time', e.target.value)}
                                             />
                                         </td>
                                         <td>
                                             <div className="flex justify-center content-center">
                                                 <input
                                                     type="checkbox"
                                                     className="toggle toggle-warning text-sm mr-4"
                                                     checked={line.is_holyday}
                                                     onChange={(e) => handleDailyHspChange(index, 'is_holyday', e.target.checked)}
                                                 />
                                                 <button type="button" onClick={() => removeLine(index)}>
                                                     <FaTrash color='red' />
                                                 </button>
                                             </div>
                                         </td>
                                     </tr>
                                 ))}
                             </tbody>
                         </table>

                        <button
                            type="button"
                            className="mt-4 text-blue-600 hover:underline"
                            onClick={addNewLine}
                        >
                            {t('hspForm.addLine')}
                        </button>
                    </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-4">
                    <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => setIsDrawerOpen(false)}
                    >
                        {t('basePrimeForm.cancel')}
                    </button>
                    <button type="submit" className="btn btn-outline bg-[--color-primary] text-white">
                        {id ? t('hspForm.update') : t('hspForm.add')}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default HspForm;
